import { useCharacterStore } from '@/stores/characterStore'
import { useCampaignStore } from '@/stores/campaignStore'

export function buildViewScopedContext({
  character,
  campaign,
  route,
  selectionText,
  topicState
} = {}) {
  // Try to resolve stores if explicit character/campaign not provided
  try {
    if (!character) {
      const cs = useCharacterStore()
      character = cs?.currentCharacter || cs?.getCurrentCharacter || null
      if (typeof character === 'function') character = character()
    }
  } catch {
    /* ignore store lookup errors */
  }
  try {
    if (!campaign) {
      const camp = useCampaignStore()
      campaign = camp?.getCurrentCampaign || camp?.currentCampaign || null
      if (typeof campaign === 'function') campaign = campaign()
    }
  } catch {
    /* ignore store lookup errors */
  }

  const ctx = {
    character: character ? summarizeCharacter(character) : null,
    campaign: campaign ? summarizeCampaign(campaign) : null,
    route: route ? summarizeRelevantRoute(route) : null,
    // Cap selection to 500 chars — large pastes should use file upload paths.
    // Only include when non-empty to avoid sending an empty string.
    selection: selectionText ? String(selectionText).slice(0, 500) : undefined,
    topicState: topicState ? summarizeTopicState(topicState) : null,
    memoryHints: summarizeMemoryHints({
      topicState,
      character,
      campaign,
      selectionText
    })
  }
  return prune(ctx, 8000)
}

function summarizeCharacter(c) {
  if (!c || typeof c !== 'object') return null
  const id = c.id || c._id || null
  const name = c.name || ''
  const level =
    c.level ||
    (Array.isArray(c.classes) ? c.classes.reduce((n, cl) => n + (Number(cl.level) || 0), 0) : null)
  const race = c.race || c.ancestry || null
  const classes = Array.isArray(c.classes)
    ? c.classes.map((cl) => ({ name: cl.name, level: cl.level }))
    : []
  const classesText = Array.isArray(c.classes)
    ? c.classes.map((cl) => `${cl.name || ''} ${cl.level || 0}`.trim()).join(', ')
    : ''
  return { id, name, level, race, classes, classesText }
}

function summarizeCampaign(c) {
  if (!c || typeof c !== 'object') return null
  const id = c.id || null
  const title = c.title || c.name || ''
  const dm = c.dm || null
  return { id, title, dm }
}

function summarizeRoute(r) {
  const { name, path, params } = r || {}
  return { name, path, params }
}

// Routes where the current page is meaningfully different from the default view
// and worth telling the AI about (e.g. the user is on a character sheet or spell page).
const CONTEXT_RELEVANT_ROUTES = new Set([
  'character-sheet',
  'character-detail',
  'campaign-details',
  'campaign-detail',
  'spell-details',
  'spell-detail',
  'monster-details',
  'monster-detail',
  'class-details',
  'class-detail',
  'note-detail',
  'vault-detail'
])

function summarizeRelevantRoute(r) {
  const { name, path, params } = r || {}
  if (!name || !CONTEXT_RELEVANT_ROUTES.has(name)) return null
  return { name, path, params }
}

function summarizeTopicState(state) {
  if (!state || typeof state !== 'object') return null
  return {
    intentHint: String(state.intentHint || '').trim() || null,
    queryKind: String(state.queryKind || '').trim() || null,
    responseMode: String(state.responseMode || '').trim() || null,
    lookupQueryHint: String(state.lookupQueryHint || '').trim() || null,
    canonicalQueryHint: String(state.canonicalQueryHint || '').trim() || null,
    focusQuery: String(state.focusQuery || '').trim() || null,
    focusEntityType: String(state.focusEntityType || '').trim() || null,
    resolvedEntityType: String(state.resolvedEntityType || '').trim() || null,
    resolvedEntityId: String(state.resolvedEntityId || '').trim() || null,
    resolvedEntityName: String(state.resolvedEntityName || '').trim() || null,
    vaultName: String(state.vaultName || '').trim() || null,
    personalDataTargetType: String(state.personalDataTargetType || '').trim() || null,
    selectionLock: summarizeSelectionLock(state.selectionLock),
    clarificationState: summarizeClarificationState(state.clarificationState)
  }
}

function summarizeSelectionLock(lock) {
  if (!lock || typeof lock !== 'object') return null
  const entityType = String(lock.entityType || '').trim().toLowerCase()
  const entityName = String(lock.entityName || '').trim()
  const lookupQueryHint = String(lock.lookupQueryHint || '').trim()
  const entityId = String(lock.entityId || '').trim()
  const lockedAt = String(lock.lockedAt || '').trim()
  if (!entityType || (!entityName && !lookupQueryHint)) return null
  return {
    entityType,
    entityId: entityId || null,
    entityName: entityName || null,
    lookupQueryHint: lookupQueryHint || null,
    lockedAt: lockedAt || null
  }
}

function summarizeClarificationState(state) {
  if (!state || typeof state !== 'object') return null
  const prompt = String(state.prompt || '').trim()
  const kind = String(state.kind || '').trim().toLowerCase() || 'clarification'
  const choices = Array.isArray(state.choices)
    ? state.choices
        .map((choice) => {
          const label = String(choice?.label || choice?.value || '').trim()
          const value = String(choice?.value || choice?.label || '').trim()
          if (!label || !value) return null
          return {
            label,
            value,
            id: String(choice?.id || '').trim() || null,
            entityType: String(choice?.entityType || '').trim().toLowerCase() || null
          }
        })
        .filter(Boolean)
        .slice(0, 5)
    : []
  if (!prompt || !choices.length) return null
  return {
    kind,
    prompt,
    choices,
    createdAt: String(state.createdAt || '').trim() || null
  }
}

function summarizeMemoryHints({ topicState, character, campaign, selectionText } = {}) {
  const normalizedSelection = String(selectionText || '').trim()
  const summary = {
    working: {
      hasSelection: Boolean(normalizedSelection),
      hasTopicState: Boolean(topicState && typeof topicState === 'object'),
      hasSelectionLock: Boolean(topicState?.selectionLock),
      hasClarificationChoices: Boolean(topicState?.clarificationState?.choices?.length),
      activeIntent: String(topicState?.intentHint || '').trim() || null
    },
    persistent: {
      hasCharacterProfile: Boolean(character && typeof character === 'object'),
      hasCampaignProfile: Boolean(campaign && typeof campaign === 'object')
    }
  }
  return summary
}

function prune(obj, maxLen) {
  const json = JSON.stringify(obj)
  if (json.length <= maxLen) return obj
  return { note: 'context_truncated', approxBytes: json.length }
}

export function estimateTokens(text = '') {
  const str = String(text || '')
  if (!str) return 0
  // CJK characters tokenize as ~1-2 tokens each rather than 1 token per 4 chars.
  const cjkCount = (str.match(/[\u3000-\u9fff\uac00-\ud7af\uf900-\ufaff]/g) || []).length
  const restLen = str.length - cjkCount
  return Math.ceil(restLen / 4 + cjkCount * 1.5)
}
